import ClusterFilterAccordionItem, {
    ClusterHelpLink,
} from 'components/ClusterFilterAccordionItem';
import {
    FilterAccordionItem,
    FilterCheckbox,
    FilterChipItemButton,
} from 'components/FilterAccordion';
import HealthStatFilterAccordionItem, {
    PopularityShareFilterOption,
    PriceHealthStatFilterOption,
} from 'components/HealthStatFilterAccordionItem';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '@/ui/design-system/components/ui/tabs';
import { healthStatFilterOptions, lifeCycleFilterOptions } from 'utils/filters';
import { Accordion } from 'components/Accordion';
import { Badge } from '@/ui/design-system/components/ui/badge';
import FlavorFilterAccordionItem from 'components/FlavorFilters/FlavorFilterAccordionItem';
import { INSTAGRAM_COUNTRIES } from '@/config/constants';
import { MetricIcon } from 'components/HealthStats';
import { POPULARITY_SORT_OPTIONS } from 'utils/metrics';
import { cn } from '@/ui/design-system/utils/cn';
import styled from 'styled-components';
import { useCurrentCountry } from 'utils/hooks';
import { useFetchFlavors } from 'utils/hooks/api';
import { useState } from 'react';

const InlineAccordion = styled(Accordion)`
    background-color: transparent;
    box-shadow: none;
    border-radius: 0;

    & > li {
        background-color: transparent;
        border-top: solid 1px var(--input);

        &:first-child {
            border-top: solid 1px var(--input);
        }

        &:hover > * {
            background-color: transparent;
        }

        &:has([aria-expanded='true']) {
            box-shadow: inset 4px 0 0 0 var(--primary);
        }
    }

    & > li > button {
        background-color: transparent;
        padding: 12px;
        font-size: 0.875rem;
        font-weight: 500;
        color: var(--foreground);

        & > span:first-child {
            color: var(--muted-foreground);
        }

        & > svg {
            color: var(--primary);
        }

        &[aria-expanded='true'] > span:first-child {
            color: var(--primary);
        }

        &:hover {
            background-color: transparent;
            opacity: 0.8;
        }
    }

    & > li > ul {
        padding-left: 12px;
        padding-right: 12px;
        padding-bottom: 8px;

        button {
            text-align: left;
        }
    }
`;

function LifeCycleCard({ option, isSelected, onToggle }) {
    return (
        <button
            type="button"
            onClick={onToggle}
            className={cn(
                'w-full text-left rounded-lg border px-3 py-3 transition-colors cursor-pointer',
                'flex flex-col gap-1',
                isSelected
                    ? 'bg-accent border-primary hover:brightness-95'
                    : 'bg-background border-input hover:bg-muted',
            )}
        >
            <span className="text-sm font-medium text-foreground">
                {option.label}
            </span>
            <span className="text-xs text-muted-foreground leading-normal">
                {option.description}
            </span>
        </button>
    );
}

function CardsFilterAccordion({
    filters,
    setFilter,
    setFilters,
    sortOption,
    setSortOption,
    initialSortOption,
    healthStatKeys,
    mixpanelMeta,
    areFlavorFiltersSupported,
    data,
    isLoadingData,
}) {
    const [activeTab, setActiveTab] = useState('life_cycle');
    const [activeAdvancedSection, setActiveAdvancedSection] = useState(null);
    const country = useCurrentCountry();
    const isInstagramSupported =
        country && INSTAGRAM_COUNTRIES.includes(country.text_id);

    const lifeCycleFilters = filters.life_cycle || [];
    const advancedFilterCount = Object.entries(filters).reduce(
        (acc, [key, value]) => {
            if (
                key === 'name' ||
                key === 'id' ||
                key === 'life_cycle' ||
                !value
            )
                return acc;
            return acc + value.length;
        },
        0,
    );

    const getOnChange =
        (filterKey) =>
        ({ action, option }) => {
            switch (action) {
                case 'select-option':
                    setFilter(filterKey, [
                        ...(filters[filterKey] || []),
                        option,
                    ]);
                    break;
                case 'remove-value':
                    setFilter(
                        filterKey,
                        filters[filterKey].filter(
                            ({ value }) => value !== option.value,
                        ),
                    );
                    break;
                default:
                    throw new Error(`Unexpected action type "${action}"`);
            }
        };

    const handleLifeCycleToggle = (option) => {
        const isSelected = lifeCycleFilters.some(
            ({ value }) => value === option.value,
        );
        if (isSelected) {
            const remaining = lifeCycleFilters.filter(
                ({ value }) => value !== option.value,
            );
            setFilter('life_cycle', remaining);
            if (remaining.length === 0) {
                setSortOption(initialSortOption);
            }
        } else {
            setFilter('life_cycle', [...lifeCycleFilters, option]);
            setSortOption(POPULARITY_SORT_OPTIONS.increase_YoY);
        }
    };

    const flavorsQuery = useFetchFlavors();
    const groupedFlavorsOptions =
        flavorsQuery.data?.groupedFlavorsOptions || {};

    const filteredHealthStatFilterOptions = healthStatKeys
        ? healthStatFilterOptions.filter(({ label }) =>
              healthStatKeys.includes(label),
          )
        : healthStatFilterOptions;

    return (
        <div className="rounded-lg border border-input overflow-hidden">
            <div className="bg-card">
                <Tabs
                    value={activeTab}
                    onValueChange={setActiveTab}
                    className="w-full"
                >
                    <TabsList className="mx-3 mt-3 mb-3 w-[calc(100%-24px)]">
                        <TabsTrigger value="life_cycle" className="flex-1">
                            Life cycle
                            {lifeCycleFilters.length > 0 &&
                                activeTab !== 'life_cycle' && (
                                    <Badge className="rounded-full min-w-[20px] h-5 px-1 text-xs">
                                        {lifeCycleFilters.length}
                                    </Badge>
                                )}
                        </TabsTrigger>
                        <TabsTrigger value="advanced" className="flex-1">
                            Advanced
                            {advancedFilterCount > 0 &&
                                activeTab !== 'advanced' && (
                                    <Badge className="rounded-full min-w-[20px] h-5 px-1 text-xs">
                                        {advancedFilterCount}
                                    </Badge>
                                )}
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent
                        value="life_cycle"
                        className="mt-0 px-3 data-[state=active]:pb-3 flex flex-col gap-2"
                    >
                        {lifeCycleFilterOptions.map((option) => (
                            <LifeCycleCard
                                key={option.value}
                                option={option}
                                isSelected={lifeCycleFilters.some(
                                    ({ value }) => value === option.value,
                                )}
                                onToggle={() => handleLifeCycleToggle(option)}
                            />
                        ))}
                    </TabsContent>

                    <TabsContent value="advanced" className="mt-0">
                        <InlineAccordion
                            $isActive={
                                advancedFilterCount > 0 ||
                                !!activeAdvancedSection
                            }
                        >
                            {areFlavorFiltersSupported && (
                                <FlavorFilterAccordionItem
                                    isOpen={activeAdvancedSection?.includes(
                                        'flavors',
                                    )}
                                    toggleOpen={() =>
                                        setActiveAdvancedSection(
                                            activeAdvancedSection === 'flavors'
                                                ? null
                                                : 'flavors',
                                        )
                                    }
                                    active={activeAdvancedSection}
                                    nestedToggleOpen={(val) =>
                                        setActiveAdvancedSection(
                                            activeAdvancedSection === val
                                                ? 'flavors'
                                                : val,
                                        )
                                    }
                                    filters={filters}
                                    getOnChange={getOnChange('flavors')}
                                    options={groupedFlavorsOptions}
                                    mixpanelMeta={mixpanelMeta}
                                    panelId="cards-filter-flavors"
                                    setFilter={setFilter}
                                    data={data}
                                    isLoadingData={isLoadingData}
                                />
                            )}
                            <ClusterFilterAccordionItem
                                isOpen={activeAdvancedSection === 'cluster'}
                                toggleOpen={() =>
                                    setActiveAdvancedSection(
                                        activeAdvancedSection === 'cluster'
                                            ? null
                                            : 'cluster',
                                    )
                                }
                                appliedFilters={
                                    filters.cluster
                                        ? filters.cluster.map(
                                              ({ value }) => value,
                                          )
                                        : null
                                }
                                onChange={getOnChange('cluster')}
                                mixpanelMeta={mixpanelMeta}
                                panelId="cards-filter-clusters"
                            >
                                <ClusterHelpLink
                                    href="https://help.spate.nyc/en/article/clusters-explained"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    mixpanelContext="CLUSTERS_HELP"
                                >
                                    What are clusters?
                                </ClusterHelpLink>
                            </ClusterFilterAccordionItem>
                            {filteredHealthStatFilterOptions.map(
                                ({
                                    label,
                                    filter: filterKey,
                                    options,
                                    panelId,
                                    abbrTitleHTML,
                                }) => {
                                    let FilterOption;
                                    let filteredOptions = options;

                                    if (label === 'price point') {
                                        FilterOption =
                                            PriceHealthStatFilterOption;
                                    } else if (label === 'popularity share') {
                                        FilterOption =
                                            PopularityShareFilterOption;
                                        if (!isInstagramSupported) {
                                            filteredOptions = options.filter(
                                                (option) =>
                                                    option.label !==
                                                    'Instagram',
                                            );
                                        }
                                    }
                                    return (
                                        <HealthStatFilterAccordionItem
                                            isOpen={
                                                activeAdvancedSection === label
                                            }
                                            toggleOpen={() =>
                                                setActiveAdvancedSection(
                                                    activeAdvancedSection ===
                                                        label
                                                        ? null
                                                        : label,
                                                )
                                            }
                                            appliedFilters={
                                                filters[filterKey]
                                                    ? filters[filterKey]
                                                          .filter(({ value }) =>
                                                              filteredOptions.some(
                                                                  ({
                                                                      value: optionValue,
                                                                  }) =>
                                                                      value ===
                                                                      optionValue,
                                                              ),
                                                          )
                                                          .map(
                                                              ({ value }) =>
                                                                  value,
                                                          )
                                                    : null
                                            }
                                            onChange={getOnChange(filterKey)}
                                            label={label}
                                            panelId={`cards-filter-health-stats-${panelId}`}
                                            abbrTitleHTML={abbrTitleHTML}
                                            options={filteredOptions}
                                            Icon={() => (
                                                <MetricIcon
                                                    metricLabel={label}
                                                    narrow={true}
                                                />
                                            )}
                                            mixpanelMeta={mixpanelMeta}
                                            key={label}
                                            {...(FilterOption && {
                                                FilterOption,
                                            })}
                                        />
                                    );
                                },
                            )}
                        </InlineAccordion>
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}

export default CardsFilterAccordion;
